import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking; false = anon; obj = admin
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data?.access_token) localStorage.setItem("tw_token", data.access_token);
      // The /auth/login response only carries the minimal user blob; re-fetch /auth/me so the
      // context has the up-to-date permission map + is_super_admin flag.
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* noop */ }
    localStorage.removeItem("tw_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Permission helper — returns true when the current user is allowed to act at `required`
 * level on `resource`. Super admins always pass.
 * Levels (ascending): no_access < view < manage < delete.
 */
const LEVEL_ORDER = ["no_access", "view", "manage", "delete"];
export function useCan() {
  const { user } = useAuth();
  return (resource, required = "view") => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const perms = user.permissions || {};
    const granted = perms[resource] || "no_access";
    return LEVEL_ORDER.indexOf(granted) >= LEVEL_ORDER.indexOf(required);
  };
}
